'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { taskListMachine } from './machines/taskListMachine'
import { getFilteredTasks, getSortedTasks } from './task-filter-utils'
import type { Task, Board, BoardColumn, TaskFilters, TaskSort } from './types'

type TaskMachineApi = ReturnType<typeof useMachine<typeof taskListMachine>>

const TaskListMachineContext = createContext<TaskMachineApi | null>(null)

export function TaskListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(taskListMachine)
  return <TaskListMachineContext.Provider value={api}>{children}</TaskListMachineContext.Provider>
}

export function useTaskListStoreCompat() {
  const machineApi = useContext(TaskListMachineContext)
  if (!machineApi) {
    throw new Error('useTaskListStoreCompat must be used within TaskListMachineProvider')
  }

  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context
    return {
      tasks: ctx.tasks,
      selectedTaskIds: ctx.selectedTaskIds,
      currentBoard: ctx.currentBoard,
      boards: ctx.boards,
      viewMode: ctx.viewMode,
      filters: ctx.filters,
      sort: ctx.sort,
      dragState: ctx.dragState,
      setTasks: (tasks: Task[]) => send({ type: 'SET_TASKS', tasks }),
      addTask: (task: Task) => send({ type: 'ADD_TASK', task }),
      updateTask: (id: string, updates: Partial<Task>) =>
        send({ type: 'UPDATE_TASK', id, updates }),
      removeTask: (id: string) => send({ type: 'REMOVE_TASK', id }),
      setSelectedTaskIds: (ids: string[]) => send({ type: 'SET_SELECTED_TASK_IDS', ids }),
      toggleTaskSelection: (id: string) => send({ type: 'TOGGLE_TASK_SELECTION', id }),
      clearSelection: () => send({ type: 'CLEAR_SELECTION' }),
      setCurrentBoard: (board: Board | null) => send({ type: 'SET_CURRENT_BOARD', board }),
      setBoards: (boards: Board[]) => send({ type: 'SET_BOARDS', boards }),
      updateBoard: (id: string, updates: Partial<Board>) =>
        send({ type: 'UPDATE_BOARD', id, updates }),
      addColumn: (column: BoardColumn) => send({ type: 'ADD_COLUMN', column }),
      updateColumn: (id: string, updates: Partial<BoardColumn>) =>
        send({ type: 'UPDATE_COLUMN', id, updates }),
      removeColumn: (id: string) => send({ type: 'REMOVE_COLUMN', id }),
      reorderColumns: (startIndex: number, endIndex: number) =>
        send({ type: 'REORDER_COLUMNS', startIndex, endIndex }),
      setViewMode: (mode: 'list' | 'board') => send({ type: 'SET_VIEW_MODE', mode }),
      setFilters: (filters: Partial<TaskFilters>) => send({ type: 'SET_FILTERS', filters }),
      clearFilters: () => send({ type: 'CLEAR_FILTERS' }),
      setSort: (sort: Partial<TaskSort>) => send({ type: 'SET_SORT', sort }),
      startDrag: (taskId: string, columnId: string) =>
        send({ type: 'START_DRAG', taskId, columnId }),
      endDrag: () => send({ type: 'END_DRAG' }),
      setDragTarget: (columnId: string | null) => send({ type: 'SET_DRAG_TARGET', columnId }),
      moveTask: (taskId: string, targetColumnId: string, position?: number) =>
        send({ type: 'MOVE_TASK', taskId, targetColumnId, position }),
      getTaskById: (id: string) => ctx.tasks.find((t) => t.id === id),
      getTasksByColumn: (columnId: string) => ctx.tasks.filter((t) => t.columnId === columnId),
      getFilteredTasks: () => getFilteredTasks(ctx.tasks, ctx.filters),
      getSortedTasks: (tasks: Task[]) => getSortedTasks(tasks, ctx.sort),
    }
  }, [machineApi])
}

export function useTaskActions() {
  const machineApi = useContext(TaskListMachineContext)
  if (!machineApi) {
    throw new Error('useTaskActions must be used within TaskListMachineProvider')
  }
  const send = machineApi[1]

  return useMemo(() => ({
    setTasks: (tasks: Task[]) => send({ type: 'SET_TASKS', tasks }),
    addTask: (task: Task) => send({ type: 'ADD_TASK', task }),
    updateTask: (id: string, updates: Partial<Task>) =>
      send({ type: 'UPDATE_TASK', id, updates }),
    removeTask: (id: string) => send({ type: 'REMOVE_TASK', id }),
    setSelectedTaskIds: (ids: string[]) => send({ type: 'SET_SELECTED_TASK_IDS', ids }),
    toggleTaskSelection: (id: string) => send({ type: 'TOGGLE_TASK_SELECTION', id }),
    clearSelection: () => send({ type: 'CLEAR_SELECTION' }),
    setCurrentBoard: (board: Board | null) => send({ type: 'SET_CURRENT_BOARD', board }),
    setBoards: (boards: Board[]) => send({ type: 'SET_BOARDS', boards }),
    updateBoard: (id: string, updates: Partial<Board>) =>
      send({ type: 'UPDATE_BOARD', id, updates }),
    addColumn: (column: BoardColumn) => send({ type: 'ADD_COLUMN', column }),
    updateColumn: (id: string, updates: Partial<BoardColumn>) =>
      send({ type: 'UPDATE_COLUMN', id, updates }),
    removeColumn: (id: string) => send({ type: 'REMOVE_COLUMN', id }),
    reorderColumns: (startIndex: number, endIndex: number) =>
      send({ type: 'REORDER_COLUMNS', startIndex, endIndex }),
    setViewMode: (mode: 'list' | 'board') => send({ type: 'SET_VIEW_MODE', mode }),
    setFilters: (filters: Partial<TaskFilters>) => send({ type: 'SET_FILTERS', filters }),
    clearFilters: () => send({ type: 'CLEAR_FILTERS' }),
    setSort: (sort: Partial<TaskSort>) => send({ type: 'SET_SORT', sort }),
    startDrag: (taskId: string, columnId: string) =>
      send({ type: 'START_DRAG', taskId, columnId }),
    endDrag: () => send({ type: 'END_DRAG' }),
    setDragTarget: (columnId: string | null) => send({ type: 'SET_DRAG_TARGET', columnId }),
    moveTask: (taskId: string, targetColumnId: string, position?: number) =>
      send({ type: 'MOVE_TASK', taskId, targetColumnId, position }),
  }), [send])
}

export function useCurrentBoard() {
  const store = useTaskListStoreCompat()
  return store.currentBoard
}

export function useViewMode() {
  const store = useTaskListStoreCompat()
  return store.viewMode
}

export function useDragState() {
  const store = useTaskListStoreCompat()
  return store.dragState
}

export function useTaskFilters() {
  const store = useTaskListStoreCompat()
  return store.filters
}

export function useSelectedTaskIds() {
  const store = useTaskListStoreCompat()
  return store.selectedTaskIds
}

export function useIsTaskDragging(taskId: string) {
  const { dragState } = useTaskListStoreCompat()
  return dragState.isDragging && dragState.draggedTaskId === taskId
}

export function useIsColumnTarget(columnId: string) {
  const { dragState } = useTaskListStoreCompat()
  return dragState.isDragging && dragState.targetColumnId === columnId
}

/** @deprecated use useTaskListStoreCompat */
export const useTaskStoreCompat = useTaskListStoreCompat
